import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Table, Radio, GetProp } from 'antd';
import type { RadioChangeEvent, TableProps } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { CheckCircleOutlined } from '@ant-design/icons';

import * as actions from './store/actions';

import { AppDispatch, RootState } from '../../store';

import { NCRoamPageRequest, NCRoamUser } from './models';

const { Column } = Table;

export type NCRoamUsersListComponentProps = {
};

type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: SorterResult<any>['field'];
  sortOrder?: SorterResult<any>['order'];
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

const NCRoamUsersListComponent = ({ }: NCRoamUsersListComponentProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { ncRoamUsersResult, ncRoamUsersLoading } = useSelector(
    (state: RootState) => state.ncRoamSubs,
  );

  const renderTotal = (total: number, range: [number, number]) => {
    return `Showing ${range[0]}-${range[1]} of ${total} events`;
  };

  const [usersType, setUsersType] = useState<string>('agents');

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: renderTotal,
      current: 1,
      pageSize: 10,
    },
  });

  const loadData = () => {
    const currentPageIndex = tableParams.pagination?.current ? tableParams.pagination.current - 1 : 1;
    const pageSize = tableParams.pagination?.pageSize ? tableParams.pagination.pageSize : 10;

    const requestParams: NCRoamPageRequest = {
      pageIndex: currentPageIndex,
      pageSize: pageSize,
      sortBy: tableParams.sortField?.toString(),
      sortDirection: tableParams.sortOrder?.toString(),
      type: usersType,
    };

    dispatch(actions.getNCRoamUsers(requestParams)).then((res: any) => {
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: res.totalCount,
        },
      });
    });
  };

  useEffect(() => {
    loadData();
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams?.sortOrder,
    tableParams?.sortField,
    JSON.stringify(tableParams.filters),
    usersType
  ]);

  const handleTableChange: TableProps<NCRoamUser>['onChange'] = (pagination, filters, sorter) => {
    setTableParams({
      pagination: {
        ...tableParams.pagination,
        ...pagination,
      },
      filters,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
  };

  const onUserTypeChange = (e: RadioChangeEvent) => {
    setUsersType(e.target.value);

    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    });
  };

  return (
    <>
      <Radio.Group onChange={onUserTypeChange} defaultValue={usersType} buttonStyle='solid'>
        <Radio.Button value="agents">Agents</Radio.Button>
        <Radio.Button value="insureds">Insureds</Radio.Button>
      </Radio.Group>

      <Table<NCRoamUser>
        dataSource={ncRoamUsersResult ?? []}
        loading={ncRoamUsersLoading}
        rowKey="id"
        size="small"
        pagination={tableParams.pagination}
        onChange={handleTableChange}
      >
        {usersType === 'agents'
          ? <>
            <Column
              title="First Name"
              key="firstName"
              dataIndex={'firstName'}
              sorter={false}
              width={100}
            />
            <Column
              title="Last Name"
              key="lastName"
              dataIndex={'lastName'}
              sorter={false}
              width={100}
            />
          </>
          : <Column
            title="Full Name"
            key="fullName"
            dataIndex={'fullName'}
            sorter={false}
            width={100}
          />}
        <Column
          title="User Name"
          key="userName"
          dataIndex={'userName'}
          sorter={true}
          width={100}
        />
        <Column
          title="Has Access"
          key="hasAccess"
          dataIndex={'hasAccess'}
          sorter={true}
          width={100}
          render={(hasAccess, record) => {
            return <>{hasAccess ? <CheckCircleOutlined style={{ color: 'green' }} /> : null}</>;
          }}
        />
      </Table >
    </>
  );
};

export default NCRoamUsersListComponent;
